import {
  RD_SCHEDULE_FAILURE_COPY,
  type RdScheduleActionAcceptedResponse,
  type RdScheduleActionFailure,
  type RdScheduleActionOptionsResponse,
  type RdScheduleActionRequestBody,
  type RdScheduleActionStatusResponse,
  type RdScheduleAppointmentType,
  type RdScheduleAppointmentTypesResponse,
  type RdScheduleClient,
  type RdScheduleClientsResponse,
  type RdScheduleOpenTimesResponse,
  type RdScheduleOutcome,
  type RdSchedulePreviewResponse,
  type RdScheduleRefusalResponse,
  type RdScheduleRepeatInterval,
} from "../../../shared/rd-schedule-actions";

// NO-876. The client side of the RD Schedule action API (NO-872): request
// paths, the submit-and-wait loop, the words each outcome shows, and the
// confirm-button names. A leaf module that imports only the dependency-free
// shared contract so `tsx --test` reads it; the dialogs stay thin renderers.

export type ScheduleApi = <T>(path: string, init?: RequestInit) => Promise<T>;

const BASE = "/api/rd-schedule";

/** Supervisors and admins act on one RD through `?rdId=`; an RD acts on themself. */
export function schedulePath(
  path: string,
  rdId: string | null | undefined,
  params: Record<string, string | number | boolean | null | undefined> = {}
): string {
  const query = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== "") {
      query.set(key, String(value));
    }
  }

  if (rdId) query.set("rdId", rdId);

  const text = query.toString();

  return text ? `${BASE}${path}?${text}` : `${BASE}${path}`;
}

export function fetchOpenTimes(
  api: ScheduleApi,
  input: {
    rdId?: string | null;
    date: string;
    appointmentTypeId: string;
    excludeAppointmentId?: string;
  }
): Promise<RdScheduleOpenTimesResponse> {
  return api(
    schedulePath("/open-times", input.rdId, {
      date: input.date,
      appointmentTypeId: input.appointmentTypeId,
      excludeAppointmentId: input.excludeAppointmentId,
    })
  );
}

export async function fetchAppointmentTypes(
  api: ScheduleApi,
  rdId?: string | null
): Promise<RdScheduleAppointmentType[]> {
  const response = await api<RdScheduleAppointmentTypesResponse>(
    schedulePath("/appointment-types", rdId)
  );

  return response.data;
}

export async function searchClients(
  api: ScheduleApi,
  rdId: string | null | undefined,
  query: string
): Promise<RdScheduleClient[]> {
  const response = await api<RdScheduleClientsResponse>(
    schedulePath("/clients", rdId, { query })
  );

  return response.data;
}

export function fetchPreview(
  api: ScheduleApi,
  input: {
    rdId?: string | null;
    appointmentTypeId: string;
    startsAt: string;
    outsideHours: boolean;
    repeat: { interval: RdScheduleRepeatInterval; count: number } | null;
  }
): Promise<RdSchedulePreviewResponse> {
  return api(
    schedulePath("/preview", input.rdId, {
      appointmentTypeId: input.appointmentTypeId,
      startsAt: input.startsAt,
      outsideHours: input.outsideHours,
      repeatInterval: input.repeat?.interval,
      repeatCount: input.repeat?.count,
    })
  );
}

export function fetchActionOptions(
  api: ScheduleApi,
  rdId: string | null | undefined,
  appointmentId: string
): Promise<RdScheduleActionOptionsResponse> {
  return api(schedulePath(`/appointments/${appointmentId}/options`, rdId));
}

// --- Submit and wait ---------------------------------------------------------

/** What the dialog shows when an action does not go through. */
export interface ActionProblem {
  message: string;
  fix: string;
  /** A slot was taken; the dialog reloads its open times. */
  slotConflict: boolean;
  /** Dates that clash, for a series refused up front. */
  dates: string[];
}

/**
 * How the next confirm continues an action that did not finish. `retry`
 * sends the saved action back through the retry route, which keeps the targets
 * it froze when the RD first confirmed; `poll` only waits for it again. A
 * fresh submit would rebuild the targets from today's dates and could move a
 * partly changed series twice.
 */
export interface ActionResume {
  actionId: string;
  mode: "retry" | "poll";
}

export type ActionResult =
  | { ok: true; healthieAppointmentIds: string[] }
  | {
      ok: false;
      problem: ActionProblem;
      resume: ActionResume | null;
      /**
       * Some visits were already changed and the rest cannot be continued. The
       * form is stale: a fresh submit would shift the changed visits again.
       */
      partial: boolean;
    };

const REFUSAL_FIX: Record<string, string> = {
  slot_taken: "Pick another open time.",
  occurrence_unavailable: "Pick another time or change how many visits repeat.",
  appointment_not_actionable:
    "Close this and open the visit again to see where it stands.",
  series_too_long: "Choose only this visit.",
  appointment_length_unknown: "Pick a different appointment type.",
  series_overlaps_itself: "Pick a different repeat, or fewer visits.",
  outcome_not_available: "Pick one of the other outcomes.",
  medicaid_not_charged: "Choose not to charge the fee.",
  invalid_request: "Check the form and try again.",
};

const SLOT_CONFLICT_CODES = new Set(["slot_taken", "occurrence_unavailable"]);

const GENERIC_PROBLEM: ActionProblem = {
  message: "That did not go through.",
  fix: "Try again. Nothing was changed.",
  slotConflict: false,
  dates: [],
};

const OFFLINE_PROBLEM: ActionProblem = {
  message: "AlwaysAI could not be reached.",
  fix: "Check your connection and try again.",
  slotConflict: false,
  dates: [],
};

interface ErrorLike {
  status?: unknown;
  body?: unknown;
  name?: unknown;
}

/** Turns a thrown API error into plain copy; unknown shapes read as a generic retry. */
export function problemFromError(error: unknown): ActionProblem {
  const { status, body, name } = (error ?? {}) as ErrorLike;

  if (name === "NetworkRequestError") return OFFLINE_PROBLEM;

  if (typeof status !== "number") return GENERIC_PROBLEM;

  if (status === 404) {
    return {
      message: "This visit or schedule is not available any more.",
      fix: "Close this and reload your week.",
      slotConflict: false,
      dates: [],
    };
  }

  const refusal = (body ?? {}) as Partial<RdScheduleRefusalResponse>;
  const code = typeof refusal.code === "string" ? refusal.code : "";
  const message = typeof refusal.error === "string" ? refusal.error : null;
  const dates = Array.isArray(refusal.dates) ? refusal.dates : [];

  if (!message) return GENERIC_PROBLEM;

  return {
    message,
    fix: REFUSAL_FIX[code] ?? "Close this and try the change again.",
    slotConflict: SLOT_CONFLICT_CODES.has(code),
    dates,
  };
}

function problemFromFailure(failure: RdScheduleActionFailure | null): ActionProblem {
  if (!failure) return GENERIC_PROBLEM;

  const copy = RD_SCHEDULE_FAILURE_COPY[failure.code];

  return {
    message: copy?.message ?? failure.message,
    fix: copy?.fix ?? failure.fix,
    slotConflict: failure.code === "slot_taken",
    dates: [],
  };
}

/** Where an action stands while the dialog waits on it. */
export type ActionProgress = "saving" | "pending" | "running";

/** The confirm button while an action is in flight, by the action's real state. */
export function progressLabel(progress: ActionProgress): string {
  if (progress === "pending") return "Waiting to send…";

  return progress === "running" ? "Sending…" : "Saving…";
}

/** Mirrors RD_SCHEDULE_ACTION_UNSTARTED_STALE_SECONDS on the server: a pending action this old is offered a retry. */
export const ACTION_UNSTARTED_STALE_SECONDS = 600;

export interface WaitOptions {
  /** Called with the action's state on every status read. */
  onProgress?: (progress: ActionProgress) => void;
  now?: () => number;
  /** A wait that runs out asks for the retry route next, not another wait. */
  escalateToRetry?: boolean;
  /** Overrides the 90 second cap on one wait. */
  maxElapsedMs?: number;
  sleep?: (milliseconds: number) => Promise<void>;
  intervalMs?: number;
  maxAttempts?: number;
}

const POLL_INTERVAL_MS = 800;
const POLL_MAX_ATTEMPTS = 75;
/** The longest one wait lasts, so a connection where every read hangs cannot hold the dialog for minutes. */
const POLL_MAX_ELAPSED_MS = 90_000;
/** One status read that hangs counts as a failed read and the wait goes on. */
const STATUS_READ_TIMEOUT_MS = 10_000;

const PENDING_WAIT_PROBLEM: ActionProblem = {
  message: "Healthie is taking longer than usual.",
  fix: "Your change is saved. Keep this open and confirm again to keep waiting for it.",
  slotConflict: false,
  dates: [],
};

const UNCERTAIN_SUBMIT_PROBLEM: ActionProblem = {
  message: "We could not confirm that your change was received.",
  fix: "It may have gone through. Close this and check your week before trying again.",
  slotConflict: false,
  dates: [],
};

const RETRY_REFUSED_FIX =
  "This change could not be continued. Close this and check your week before trying again.";

const PARTIAL_PROBLEM: ActionProblem = {
  message: "Part of this change went through.",
  fix: "The visits already changed stay changed. Close this and check your week before changing the rest.",
  slotConflict: false,
  dates: [],
};

/** True for the note a wait that ran out shows, so the dialog can report it once. */
export function isWaitTimeout(problem: ActionProblem): boolean {
  return problem === PENDING_WAIT_PROBLEM;
}

function defaultSleep(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

/** An error the RD cannot fix by asking again: a refusal, sign-out or missing action. */
function isFinalError(error: unknown): boolean {
  const { status } = (error ?? {}) as ErrorLike;

  return typeof status === "number" && status >= 400 && status < 500;
}

/**
 * Posts one action and waits for the worker to settle it. A 202 only means
 * the change was saved; the dialog stays open until Healthie says yes or no.
 */
export async function submitAndWait(
  api: ScheduleApi,
  rdId: string | null | undefined,
  body: RdScheduleActionRequestBody,
  options: WaitOptions = {}
): Promise<ActionResult> {
  let actionId: string;

  try {
    const accepted = await api<RdScheduleActionAcceptedResponse>(
      schedulePath("/actions", rdId),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }
    );
    actionId = accepted.data.actionId;

    // NO-917: NDD already holds the change; nothing is left to wait for.
    if (accepted.data.saved === true) return { ok: true, healthieAppointmentIds: [] };
  } catch (error) {
    // Without an action id there is nothing to continue, and a lost answer
    // may hide a saved change: only a plain refusal leaves the form usable.
    const uncertain = !isFinalError(error);

    return {
      ok: false,
      problem: uncertain ? UNCERTAIN_SUBMIT_PROBLEM : problemFromError(error),
      resume: null,
      partial: uncertain,
    };
  }

  return waitForAction(api, rdId, actionId, options);
}

/** The server says the action is still in flight and has nothing to retry yet. */
function isStillRunning(error: unknown): boolean {
  const { body } = (error ?? {}) as ErrorLike;
  const code = (body as Partial<RdScheduleRefusalResponse> | undefined)?.code;

  return code === "not_retryable";
}

/**
 * Sends a failed action back through the retry route, then waits for it. A
 * retry the server refuses leaves the action as it was, possibly part
 * applied, so the form goes stale instead of offering a fresh submit.
 */
export async function retryAndWait(
  api: ScheduleApi,
  rdId: string | null | undefined,
  actionId: string,
  options: WaitOptions = {}
): Promise<ActionResult> {
  try {
    await api<RdScheduleActionAcceptedResponse>(
      schedulePath(`/actions/${actionId}/retry`, rdId),
      { method: "POST" }
    );
  } catch (error) {
    if (isStillRunning(error)) return waitForAction(api, rdId, actionId, options);

    const final = isFinalError(error);

    return {
      ok: false,
      problem: final
        ? { ...problemFromError(error), fix: RETRY_REFUSED_FIX }
        : problemFromError(error),
      resume: final ? null : { actionId, mode: "retry" },
      partial: final,
    };
  }

  return waitForAction(api, rdId, actionId, options);
}

/**
 * Polls until the worker settles the action. A failed status read does not end
 * the wait: the change is already saved, so only a refusal stops it, and
 * running out of time offers to keep waiting, never "nothing was changed".
 */
export async function waitForAction(
  api: ScheduleApi,
  rdId: string | null | undefined,
  actionId: string,
  options: WaitOptions = {}
): Promise<ActionResult> {
  const sleep = options.sleep ?? defaultSleep;
  const interval = options.intervalMs ?? POLL_INTERVAL_MS;
  const attempts = options.maxAttempts ?? POLL_MAX_ATTEMPTS;

  const deadline = Date.now() + (options.maxElapsedMs ?? POLL_MAX_ELAPSED_MS);

  for (let attempt = 0; attempt < attempts && Date.now() < deadline; attempt += 1) {
    const remaining = Math.max(deadline - Date.now(), 1);
    const settled = await readSettled(api, rdId, actionId, remaining, options);

    if (settled) return settled;

    await sleep(interval);
  }

  return {
    ok: false,
    problem: PENDING_WAIT_PROBLEM,
    resume: { actionId, mode: options.escalateToRetry ? "retry" : "poll" },
    partial: false,
  };
}

async function readSettled(
  api: ScheduleApi,
  rdId: string | null | undefined,
  actionId: string,
  remainingMs: number,
  options: WaitOptions
): Promise<ActionResult | null> {
  try {
    const status = await api<RdScheduleActionStatusResponse>(
      schedulePath(`/actions/${actionId}`, rdId),
      { signal: AbortSignal.timeout(Math.min(STATUS_READ_TIMEOUT_MS, remainingMs)) }
    );

    return settlement(status.data, options);
  } catch (error) {
    if (!isFinalError(error)) return null;

    return {
      ok: false,
      problem: problemFromError(error),
      resume: null,
      partial: true,
    };
  }
}

const UNSTARTED_PROBLEM: ActionProblem = {
  message: "This change has not started sending.",
  fix: "Retry to send it now. Your change is saved.",
  slotConflict: false,
  dates: [],
};

function settlement(
  data: RdScheduleActionStatusResponse["data"],
  options: WaitOptions
): ActionResult | null {
  if (data.status === "pending" || data.status === "running") {
    options.onProgress?.(data.status);

    const age = ((options.now?.() ?? Date.now()) - Date.parse(data.createdAt)) / 1000;

    if (data.status === "pending" && age > ACTION_UNSTARTED_STALE_SECONDS) {
      return {
        ok: false,
        problem: UNSTARTED_PROBLEM,
        resume: { actionId: data.actionId, mode: "retry" },
        partial: false,
      };
    }
  }

  if (data.status === "succeeded") {
    return { ok: true, healthieAppointmentIds: data.healthieAppointmentIds };
  }

  if (data.status === "conflict" || data.status === "failed") {
    const retryable = data.status === "failed" && data.failure?.retryable === true;
    const partial = !retryable && data.writtenCount > 0;

    return {
      ok: false,
      problem: partial ? PARTIAL_PROBLEM : problemFromFailure(data.failure),
      resume: retryable ? { actionId: data.actionId, mode: "retry" } : null,
      partial,
    };
  }

  return null;
}

/** Which call a failure came from, for the client report. Ids and values never appear. */
export type ScheduleOperation =
  | "open_times"
  | "preview"
  | "appointment_types"
  | "clients"
  | "options"
  | "submit"
  | "retry"
  | "status";

export function scheduleOperation(path: string, method = "GET"): ScheduleOperation {
  const route = path.replace(BASE, "").split("?")[0];

  if (route.endsWith("/retry")) return "retry";

  if (route.startsWith("/actions")) return method === "POST" ? "submit" : "status";

  if (route.startsWith("/appointments")) return "options";

  if (route === "/open-times") return "open_times";

  if (route === "/appointment-types") return "appointment_types";

  return route === "/preview" ? "preview" : "clients";
}

// --- Words -------------------------------------------------------------------

export const OUTCOME_COPY: Record<
  RdScheduleOutcome,
  {
    name: (client: string) => string;
    help: (hours: number, client: string) => string;
    confirm: string;
  }
> = {
  client_cancelled: {
    name: (client) => `${client} cancelled with notice`,
    help: (hours) => `More than ${hours} hours ahead. No fee.`,
    confirm: "Cancel appointment",
  },
  rd_cancelled: {
    name: () => "I'm cancelling",
    help: (_hours, client) => `No fee. ${client} is told you cancelled.`,
    confirm: "Cancel appointment",
  },
  late_cancel: {
    name: () => "Late cancel",
    help: (hours) => `Less than ${hours} hours ahead.`,
    confirm: "Mark late cancel",
  },
  no_show: {
    name: () => "No-show",
    help: (_hours, client) => `${client} didn't come. Mark this after the start time.`,
    confirm: "Mark no-show",
  },
};

export const OUTCOME_ORDER: readonly RdScheduleOutcome[] = [
  "client_cancelled",
  "rd_cancelled",
  "late_cancel",
  "no_show",
];

export function cancelConfirmLabel(outcome: RdScheduleOutcome): string {
  return OUTCOME_COPY[outcome].confirm;
}

export function isCancelOutcome(outcome: RdScheduleOutcome): boolean {
  return outcome === "client_cancelled" || outcome === "rd_cancelled";
}

export function bookConfirmLabel(visitCount: number): string {
  if (!Number.isInteger(visitCount)) return "Book appointments";

  return visitCount > 1 ? `Book ${visitCount} visits` : "Book appointment";
}

const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const WEEKDAY_LONG = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;
const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

function dateParts(isoDate: string): {
  year: number;
  month: number;
  day: number;
  weekday: number;
} {
  const [year, month, day] = isoDate.split("-").map(Number);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();

  return { year, month, day, weekday };
}

/** "Tue, Nov 3, 2026" from an RD-local `YYYY-MM-DD`. */
export function longDateLabel(isoDate: string): string {
  const { year, month, day, weekday } = dateParts(isoDate);

  return `${WEEKDAY_SHORT[weekday]}, ${MONTH_SHORT[month - 1]} ${day}, ${year}`;
}

/** "Tue, Nov 3" */
export function shortDateLabel(isoDate: string): string {
  const { month, day, weekday } = dateParts(isoDate);

  return `${WEEKDAY_SHORT[weekday]}, ${MONTH_SHORT[month - 1]} ${day}`;
}

export function weekdayName(isoDate: string): string {
  return WEEKDAY_LONG[dateParts(isoDate).weekday];
}

/** `14:30` to `2:30 PM`. */
export function timeLabel(localTime: string): string {
  const [hours, minutes] = localTime.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const twelve = hours % 12 === 0 ? 12 : hours % 12;

  return `${twelve}:${String(minutes).padStart(2, "0")} ${period}`;
}

/** "Move to Tue at 2:30 PM" */
export function moveConfirmLabel(isoDate: string, localTime: string): string {
  return `Move to ${WEEKDAY_SHORT[dateParts(isoDate).weekday]} at ${timeLabel(localTime)}`;
}

function formatIn(
  iso: string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions
): Record<string, string> {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    ...options,
  }).formatToParts(new Date(iso));

  return Object.fromEntries(parts.map((part) => [part.type, part.value]));
}

/** The RD-local date and `HH:MM` of an instant. */
export function localParts(
  iso: string,
  timeZone: string
): { date: string; time: string } {
  const p = formatIn(iso, timeZone, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });

  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
}

/** "Fri, Oct 30 at 11:00 AM" in the RD's zone. */
export function whenLabel(iso: string, timeZone: string): string {
  const { date, time } = localParts(iso, timeZone);

  return `${shortDateLabel(date)} at ${timeLabel(time)}`;
}

/**
 * The UTC instant of a wall-clock time in a zone. Two passes settle the
 * offset across a daylight-saving change.
 */
export function zonedInstant(
  isoDate: string,
  localTime: string,
  timeZone: string
): string {
  const [year, month, day] = isoDate.split("-").map(Number);
  const [hours, minutes] = localTime.split(":").map(Number);
  const wall = Date.UTC(year, month - 1, day, hours, minutes);
  let guess = wall;

  for (let pass = 0; pass < 2; pass += 1) {
    const shown = localParts(new Date(guess).toISOString(), timeZone);
    const [sy, sm, sd] = shown.date.split("-").map(Number);
    const [sh, smin] = shown.time.split(":").map(Number);
    const shownWall = Date.UTC(sy, sm - 1, sd, sh, smin);

    guess += wall - shownWall;
  }

  return new Date(guess).toISOString();
}

export function formatFee(amountCents: number | null): string | null {
  if (amountCents === null) return null;

  const dollars = amountCents / 100;

  return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}

/** "Mondays at 10:30 AM, Nov 2 to Dec 7." */
export function repeatPreviewLine(preview: RdSchedulePreviewResponse["data"]): string {
  const weekday = `${preview.weekday.charAt(0).toUpperCase()}${preview.weekday.slice(1)}s`;

  return `${weekday} at ${timeLabel(preview.localTime)}, ${monthDay(preview.firstDate)} to ${monthDay(preview.lastDate)}.`;
}

function monthDay(isoDate: string): string {
  const { month, day } = dateParts(isoDate);

  return `${MONTH_SHORT[month - 1]} ${day}`;
}

export const REPEAT_LABELS: Record<RdScheduleRepeatInterval, string> = {
  weekly: "Every week",
  biweekly: "Every 2 weeks",
  every_4_weeks: "Every 4 weeks",
};

const FEE_REASON_COPY: Record<string, (client: string) => string> = {
  first_late_cancel_this_quarter: (client) =>
    `This is ${client}'s first late cancel this quarter, so the fee is usually waived.`,
  late_cancel_limit_reached: (client) =>
    `${client} has already used the free late cancel this quarter.`,
  no_show: () => "No-shows are charged right away.",
  medicaid_never_charged: () => "Medicaid clients are never charged.",
};

/** The fee rule in plain words; a code this app does not know reads as a generic line, never raw. */
export function feeReasonLabel(reason: string, clientName: string): string {
  return FEE_REASON_COPY[reason]?.(clientName) ?? "The fee rules decide the default.";
}

/**
 * The toast an action shows once it goes through, named like the button that
 * confirmed it: "Moved to Tue at 2:30 PM", "Marked no-show", "Booked 6 visits".
 */
export function actionDoneMessage(
  body: RdScheduleActionRequestBody,
  timeZone: string
): string {
  switch (body.kind) {
    case "book":
      return body.repeat
        ? `Booked ${body.repeat.count} visits`
        : `Booked ${whenLabel(body.startsAt, timeZone)}`;

    case "reschedule": {
      const { date, time } = localParts(body.startsAt, timeZone);

      return moveConfirmLabel(date, time).replace(/^Move/, "Moved");
    }

    case "edit":
      return "Note saved";
    case "cancel":
      return CANCEL_DONE[body.outcome](body.scope === "this_and_later");
    case "delete":
      return "Appointment deleted";
  }
}

const CANCEL_DONE: Record<RdScheduleOutcome, (series: boolean) => string> = {
  client_cancelled: (series) => (series ? "Visits cancelled" : "Appointment cancelled"),
  rd_cancelled: (series) => (series ? "Visits cancelled" : "Appointment cancelled"),
  late_cancel: () => "Marked late cancel",
  no_show: () => "Marked no-show",
};
